import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/new-ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/new-ui/select';
import type { SelectOption } from '@/components/ui/select';
import { cn } from '@/lib/utils';

// Radix Select rejects an empty item value, so an option with an empty id uses the first sentinel.
// The second one is the "reset" item that clears the value.
const EMPTY_OPTION_VALUE = '__empty__';
const RESET_VALUE = '__reset__';

type OptionSelectProps = {
  id?: string;
  name?: string;
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: SelectOption[];
  placeholder?: string;
  /** Label of the first item that clears the value, for example "All" */
  resetLabel?: string;
  disabled?: boolean;
  /** Sets aria-invalid on the trigger */
  invalid?: boolean;
  /** Id of the element that describes the trigger, for example its error text */
  describedBy?: string;
  className?: string;
};

/** Single select of the redesign over `{ id, title }` options, `''` means no value */
export const OptionSelect = React.forwardRef<HTMLButtonElement, OptionSelectProps>(
  (
    {
      id,
      name,
      value,
      onChange,
      onBlur,
      options,
      placeholder,
      resetLabel,
      disabled,
      invalid,
      describedBy,
      className,
    },
    ref
  ) => {
    const current = value ?? '';
    const hasEmptyOption = options.some((option) => option.id === '');
    const showReset = Boolean(resetLabel) && !hasEmptyOption;

    return (
      <Select
        name={name}
        value={current === '' && hasEmptyOption ? EMPTY_OPTION_VALUE : current}
        onValueChange={(next) => {
          // Inside a form Radix reports '' when the value is set before its option has loaded.
          // Real empty choices come as the reset item or the sentinel
          if (next === '') return;
          if (next === RESET_VALUE || next === EMPTY_OPTION_VALUE) {
            onChange('');
            return;
          }
          onChange(next);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          ref={ref}
          id={id}
          onBlur={onBlur}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          className={className}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {showReset ? <SelectItem value={RESET_VALUE}>{resetLabel}</SelectItem> : null}
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id || EMPTY_OPTION_VALUE}>
              {option.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
);
OptionSelect.displayName = 'OptionSelect';

type OptionMultiSelectProps = {
  id?: string;
  value?: string[] | null;
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Sets aria-invalid on the trigger */
  invalid?: boolean;
  /** Id of the element that describes the trigger, for example its error text */
  describedBy?: string;
  className?: string;
};

/** Multi select of the redesign: a select-like trigger with a checkbox menu */
export const OptionMultiSelect = React.forwardRef<HTMLButtonElement, OptionMultiSelectProps>(
  (
    {
      id,
      value,
      onChange,
      onBlur,
      options,
      placeholder,
      disabled,
      invalid,
      describedBy,
      className,
    },
    ref
  ) => {
    const selected = value ?? [];
    const selectedOptions = options.filter((option) => selected.includes(option.id));
    const label = selectedOptions.length
      ? `${selectedOptions[0].title}${selected.length > 1 ? ` +${selected.length - 1}` : ''}`
      : '';

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            onBlur={onBlur}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              className
            )}
          >
            <span className={cn('line-clamp-1 text-left', !label && 'text-muted-foreground')}>
              {label || placeholder}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-72 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
        >
          {options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.id}
              checked={selected.includes(option.id)}
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(checked) =>
                onChange(
                  checked ? [...selected, option.id] : selected.filter((item) => item !== option.id)
                )
              }
            >
              {option.title}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);
OptionMultiSelect.displayName = 'OptionMultiSelect';
